import { ValidationLevel, ValidationProps } from 'ui/common/validation/types';
import { ScreenField, ScreenPicker } from 'app/area/codetable/item/screen-renderer/types';
import { ValidationData } from 'app/area/codetable/item/validation/validate';
import { CodeItem } from 'app/area/codetable/item/types-store';
import { CodetableTranslation } from 'app/area/codetable/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { createConversionFactorsId, createUomSetFieldId } from 'app/area/codetable/item/codetable-item-helpers';
import { ServerValidationErrors, ServerValidationMessage } from 'app/infrastructure/data/validation';
import { validateComplexValue } from 'app/area/codetable/item/screen-renderer/screen-validate/helpers';
import { CodeFieldWireKey } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';

const validateDefault = (
  ids: ScreenPicker[],
  codeItem: CodeItem,
  translation: CodetableTranslation
) => {

  return Object.assign({},
    ...ids.map(id => ({
      [id]: !!codeItem[id] && isDefinedIncludingZero(codeItem[id].value) ? undefined : {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      }
    })));
};

const validateUomSetField = (
  ids: ScreenPicker[],
  codeItem: CodeItem,
  translation: CodetableTranslation
) => {
  return codeItem.substanceUomSet
    && Object.assign({},
      ...ids.map(id => (codeItem.substanceUomSet.map(item => {
        const fieldId = item.uomSet && createUomSetFieldId(id, item.uomSet.value);

        if (!item[id]) {
          return {
            [fieldId]: {
              level: ValidationLevel.error,
              messages: [{
                level: ValidationLevel.error,
                message: translation.must_be_filled_out
              }]
            }
          };
        } else {
          return { [fieldId]: undefined };
        }
      }).reduce((obj, item) => Object.assign(obj, item), {}))));
};

export const validateUomSetIndicatorValue = (codeItem: CodeItem, translation: CodetableTranslation) => {
  const emptyUomIndicators = codeItem.substanceUomSet.filter(uomSet => isDefinedIncludingZero(uomSet.uomTypeIndicator?.value) &&
    !isDefinedIncludingZero(uomSet.uomIndicator?.value));

  return Object.assign({}, ...emptyUomIndicators.map(uomSet => {
    const fieldId = uomSet.uomSet && createUomSetFieldId(ScreenPicker.uomSetIndicatorValue, uomSet.uomSet.value);

    return {
      [fieldId]: {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      }
    };
  }));
};

export const mapToSubstanceConversionFactorValidation =
  (serverErrors: ServerValidationErrors): { [propertyName: string]: ValidationProps } => {
    if (!serverErrors.substanceConversionFactor) {
      return undefined;
    }

    return Object.assign({}, ...serverErrors.substanceConversionFactor?.map((error: ServerValidationMessage) => {

      const parameters = Object.keys(error.parameters) as ScreenPicker[];
      const fieldId = parameters.filter(key => key !== ScreenPicker.uomConversionType)[0];
      const tileId = parameters.filter(key => key === ScreenPicker.uomConversionType)[0];
      const uomConversionTypeId = error.parameters.uomConversionType;

      const validationId = createConversionFactorsId(fieldId ?? tileId, uomConversionTypeId);

      return {
        [validationId]: {
          level: ValidationLevel.error,
          messages: [{
            level: ValidationLevel.error,
            message: (error as ServerValidationMessage).message
          }]
        }
      };
    }));
  };

const validateConversionFactors = (
  ids: (ScreenPicker | ScreenField)[],
  codeItem: CodeItem,
  translation: CodetableTranslation
): { [propertyName: string]: ValidationProps } => {
  return codeItem.substanceConversionFactor
    && Object.assign({},
      ...ids.map(id => (codeItem.substanceConversionFactor.map(item => {
        const fieldId = item.uomConversionType && createConversionFactorsId(id, item.uomConversionType.value);

        if (!isDefinedIncludingZero(item[id])) {
          return {
            [fieldId]: {
              level: ValidationLevel.error,
              messages: [{
                level: ValidationLevel.error,
                message: translation.must_be_filled_out
              }]
            }
          };
        } else {
          return { [fieldId]: undefined };
        }
      }).reduce((obj, item) => Object.assign(obj, item), {}))));
};

const validateUomConversionFactors = (
  id: ScreenField,
  uomConversion: CodeComplexValue<number>[],
  translation: CodetableTranslation
): { [key in keyof CodeItem]?: ValidationProps } => {
  return uomConversion?.map(item => {

    const fieldId = createConversionFactorsId(id, item.value);

    return {
      [fieldId]: item[id] === undefined ? {
        level: ValidationLevel.error,
        messages: [{
          level: ValidationLevel.error,
          message: translation.must_be_filled_out
        }]
      } : undefined
    };
  }).reduce((obj, item) => Object.assign(obj, item), {});
};

export const validateSubstanceHierarchy = (validationData: ValidationData): { [key: string]: ValidationProps } => {
  const { codeItem, translation } = validationData;

  const defaultUomFieldsValidation = validateDefault([
    ScreenPicker.category,
    ScreenPicker.defaultUom,
    ScreenPicker.defaultUomType
  ], codeItem, translation);

  const uomSetFieldsValidation = validateUomSetField([
    ScreenPicker.uomSetValueType,
    ScreenPicker.uomSetValue], codeItem, translation);

  const uomSetIndicatorValueValidation = validateUomSetIndicatorValue(codeItem, translation);

  const conversionFactorsValidation = validateConversionFactors([
    ScreenPicker.uomFrom,
    ScreenPicker.uomTo,
    ScreenField.factor
  ], codeItem, translation);

  return { ...defaultUomFieldsValidation, ...uomSetFieldsValidation, ...uomSetIndicatorValueValidation, ...conversionFactorsValidation };
};

export const validateSubstanceCategory = (validationData: ValidationData): { [key: string]: ValidationProps } => {
  const { codeItem, translation } = validationData;

  const uomSetFieldsValidation = validateUomSetField([
    ScreenPicker.uomSetValueType,
    ScreenPicker.uomSetValue], codeItem, translation);

  const uomSetIndicatorValueValidation = validateUomSetIndicatorValue(codeItem, translation);

  return { ...uomSetFieldsValidation, ...uomSetIndicatorValueValidation };
};

export const validateUom = (validationData: ValidationData): { [key: string]: ValidationProps } => {
  const { codeItem, uomConversionFactors, translation, item: { isUomExcluded } } = validationData;

  const categoryValidation = validateComplexValue(CodeFieldWireKey.category, codeItem, translation);
  const conversionFactors = !isUomExcluded && validateUomConversionFactors(ScreenField.factor, uomConversionFactors, translation);

  return {
    ...categoryValidation,
    ...conversionFactors
  };
};
